import { Box, Button, Flex, Image, Skeleton, Tooltip } from "@chakra-ui/react";
import type { BoxProps, ImageProps } from "@chakra-ui/react";
import { useRef } from "react";
import { GrRefresh } from "react-icons/gr";
import { useIntl } from "react-intl";

import { useRect } from "@nens/react/hooks";

import { usePublicationThumbnailUrlQuery } from "@/queries/query-publication-thumbnail";

import { useRemovePublicationThumbnail } from "./api/remove-publication-thumbnail";
import { TIMES_WIDTH_LARGER } from "./consts/times-width-larger";

interface PublicationThumbnailProps extends BoxProps {
  publicationId: string;
  mapIsOnScreen?: boolean; // When the publication thumbnail is on the same page as the map, we can allow the user to click on the thumbnail to update it based on the current state of the map and can show a skeleton while the thumbnail is not present because we can assume its being generated
}

const DisplayThumbnailImage = (props: ImageProps) => {
  const intl = useIntl();
  return (
    <Image
      h="full"
      w="full"
      alt={intl.formatMessage({
        id: "publication.thumbnail.alt",
        defaultMessage: "Publication Thumbnail",
      })}
      {...props}
    />
  );
};

export const PublicationThumbnail = ({
  publicationId,
  mapIsOnScreen,
  ...props
}: PublicationThumbnailProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const rect = useRect(ref);

  // Always make sure the width and height are in the correct ratio, so that the thumbnail is not stretched
  return (
    <Flex
      h="full"
      alignItems="center"
      {...props}
      ref={ref}
      w={rect.height * TIMES_WIDTH_LARGER}
      rounded="base"
      overflow="hidden"
    >
      <PublicationThumbnailInner publicationId={publicationId} mapIsOnScreen={mapIsOnScreen} />
    </Flex>
  );
};

type PublicationThumbnailInnerProps = {
  publicationId: string;
  mapIsOnScreen?: boolean;
};

const PublicationThumbnailInner = ({
  publicationId,
  mapIsOnScreen,
}: PublicationThumbnailInnerProps) => {
  const intl = useIntl();
  const {
    data: thumbnailUrl,
    isSuccess,
    isLoading,
  } = usePublicationThumbnailUrlQuery(publicationId);

  const { mutate: removeThumbnail } = useRemovePublicationThumbnail();

  if (isSuccess && thumbnailUrl) {
    if (mapIsOnScreen) {
      return (
        <Tooltip
          label={intl.formatMessage({
            id: "publication.thumbnail.update_thumbnail_tooltip",
            defaultMessage:
              "Click to update thumbnail. The new thumbnail will be based on the current state of the map zoomed to the default bounds.",
          })}
          placement="top"
        >
          <Button
            onClick={() => removeThumbnail({ publicationId })}
            cursor="pointer"
            position="relative"
            p={0}
          >
            <Flex
              opacity={0}
              _hover={{ opacity: 1 }}
              transition="opacity 0.3s"
              position="absolute"
              justifyContent="center"
              alignItems="center"
              top={0}
              left={0}
              right={0}
              bottom={0}
            >
              <Box position="relative" zIndex={1} color="white" fontSize="20">
                <GrRefresh />
              </Box>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="brand.analytic"
                opacity={0.8}
              />
            </Flex>
            <DisplayThumbnailImage src={thumbnailUrl} />
          </Button>
        </Tooltip>
      );
    }
    return <DisplayThumbnailImage src={thumbnailUrl} />;
  }

  if (mapIsOnScreen && !thumbnailUrl) {
    return <Skeleton boxSize="full" />;
  }

  return (
    <Tooltip
      isDisabled={isLoading}
      label={intl.formatMessage({
        id: "publication.thumbnail.not_created_tooltip",
        defaultMessage:
          "Thumbnail has not been created yet. The thumbnail will be created when a person with write access opens the viewer.",
      })}
      placement="top"
    >
      <Box position="relative" bg="brand.light_sky" boxSize="full" />
    </Tooltip>
  );
};
